import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/PasswordInput';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertCircle, Building2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { validateEmail, validatePhone, validatePassword, validateRequired } from '../utils/validations';
import { ErrorMessage, getInputClass } from '../components/ErrorMessage';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    role: 'vendor', // Fixed to vendor only
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
    // Clear field error when typing
    setFieldErrors(prev => ({ ...prev, [name]: null }));
  };

  // Real-time validation handlers
  const handleNameBlur = () => {
    const result = validateRequired(formData.name, 'Full name');
    if (!result.valid) setFieldErrors(prev => ({ ...prev, name: result.error }));
  };

  const handleEmailBlur = () => {
    const result = validateEmail(formData.email);
    if (!result.valid) setFieldErrors(prev => ({ ...prev, email: result.error }));
  };

  const handlePhoneBlur = () => {
    if (formData.phone) {
      const result = validatePhone(formData.phone);
      if (!result.valid) setFieldErrors(prev => ({ ...prev, phone: result.error }));
    }
  };

  const handlePasswordBlur = () => {
    const result = validatePassword(formData.password);
    if (!result.valid) setFieldErrors(prev => ({ ...prev, password: result.error }));
  };

  const handleConfirmPasswordBlur = () => {
    if (formData.confirmPassword && formData.password !== formData.confirmPassword) {
      setFieldErrors(prev => ({ ...prev, confirmPassword: 'Passwords do not match' }));
    }
  };

  // Get password strength
  const getPasswordStrength = () => {
    const pwd = formData.password;
    if (!pwd) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (pwd.length >= 8) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/[a-z]/.test(pwd)) strength++;
    if (/[0-9]/.test(pwd)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) strength++;
    
    if (strength <= 2) return { strength, label: 'Weak', color: 'bg-red-500' };
    if (strength <= 3) return { strength, label: 'Medium', color: 'bg-amber-500' };
    if (strength <= 4) return { strength, label: 'Strong', color: 'bg-emerald-500' };
    return { strength, label: 'Very Strong', color: 'bg-green-600' };
  };

  const validateForm = () => {
    const errors = {};
    
    const nameResult = validateRequired(formData.name, 'Full name');
    if (!nameResult.valid) errors.name = nameResult.error;
    
    const emailResult = validateEmail(formData.email);
    if (!emailResult.valid) errors.email = emailResult.error;
    
    if (formData.phone) {
      const phoneResult = validatePhone(formData.phone);
      if (!phoneResult.valid) errors.phone = phoneResult.error;
    }
    
    const passwordResult = validatePassword(formData.password);
    if (!passwordResult.valid) errors.password = passwordResult.error;
    
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setLoading(true);
    try {
      const userData = await register({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        password: formData.password,
        role: formData.role,
      });

      toast.success('Registration successful!');
      
      // Small delay to ensure state updates propagate
      setTimeout(() => {
        // Always redirect to vendor registration since only vendors can register
        navigate('/vendor/registration');
      }, 100);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - matches landing page */}
      <div className="tricolor-strip" />
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-4">
              <img 
                src="/iith-logo.png" 
                alt="IIT Hyderabad" 
                className="h-16 w-auto"
              />
            </Link>
            <Link to="/login">
              <Button variant="outline" className="govt-btn-secondary">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="flex min-h-[calc(100vh-100px)]">
        {/* Left Side - Image */}
        <div className="hidden lg:flex lg:w-1/2 relative">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('https://images.unsplash.com/photo-1735933109524-52d7f05dc7c1?crop=entropy&cs=srgb&fm=jpg&q=85')` }}
          />
          <div className="absolute inset-0 bg-[#003380]/85" />
          <div className="relative z-10 flex flex-col justify-center p-12 text-white">
            <img 
              src="/iith-logo.png" 
              alt="IIT Hyderabad" 
              className="h-20 w-auto mb-6 bg-white p-2 rounded-lg"
            />
            <h2 className="text-3xl font-bold font-serif mb-4">
              IIT Hyderabad
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              Vendor Registration Portal
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold">1</span>
                </div>
                <div>
                  <h3 className="font-semibold">Easy Registration</h3>
                  <p className="text-sm text-blue-100">Step-by-step guided registration process</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold">2</span>
                </div>
                <div>
                  <h3 className="font-semibold">Document Upload</h3>
                  <p className="text-sm text-blue-100">Secure upload of statutory documents</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold">3</span>
                </div>
                <div>
                  <h3 className="font-semibold">Track Application</h3>
                  <p className="text-sm text-blue-100">Real-time status updates and notifications</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Form */}
        <div className="flex-1 flex items-center justify-center p-8">
          <Card className="w-full max-w-lg govt-card">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-serif govt-blue-text">Vendor Registration</CardTitle>
              <CardDescription>
                Vendor/Supplier registration for procurement activities at IIT Hyderabad
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                    <AlertCircle className="h-4 w-4" />
                    {error}
                  </div>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                  <div className="flex items-center gap-2 text-[#003380]">
                    <Building2 className="h-5 w-5" />
                    <span className="font-medium">Registering as: Vendor / Supplier</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    Register to supply goods and services to IIT Hyderabad
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleNameBlur}
                    placeholder="Enter your full name"
                    required
                    className={getInputClass(fieldErrors.name)}
                    data-testid="register-name-input"
                  />
                  <ErrorMessage error={fieldErrors.name} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleEmailBlur}
                    placeholder="Enter your email"
                    required
                    className={getInputClass(fieldErrors.email)}
                    data-testid="register-email-input"
                  />
                  <ErrorMessage error={fieldErrors.email} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number (Optional)</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    onBlur={handlePhoneBlur}
                    placeholder="10-digit mobile number"
                    maxLength={10}
                    className={getInputClass(fieldErrors.phone)}
                    data-testid="register-phone-input"
                  />
                  <ErrorMessage error={fieldErrors.phone} />
                  <p className="text-xs text-slate-500">Must start with 6, 7, 8, or 9</p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="password">Password *</Label>
                    <PasswordInput
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      onBlur={handlePasswordBlur}
                      placeholder="Create password"
                      required
                      className={getInputClass(fieldErrors.password)}
                      data-testid="register-password-input"
                    />
                    <ErrorMessage error={fieldErrors.password} />
                    {formData.password && (
                      <div className="space-y-1">
                        <div className="flex gap-1">
                          {[1, 2, 3, 4, 5].map((i) => (
                            <div
                              key={i}
                              className={`h-1 flex-1 rounded ${
                                i <= getPasswordStrength().strength
                                  ? getPasswordStrength().color
                                  : 'bg-slate-200'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-xs text-slate-500">
                          Strength: {getPasswordStrength().label}
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password *</Label>
                    <PasswordInput
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      onBlur={handleConfirmPasswordBlur}
                      placeholder="Confirm password"
                      required
                      className={getInputClass(fieldErrors.confirmPassword)}
                      data-testid="register-confirm-password-input"
                    />
                    <ErrorMessage error={fieldErrors.confirmPassword} />
                    {formData.confirmPassword && formData.password === formData.confirmPassword && (
                      <p className="text-xs text-emerald-600 flex items-center gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Passwords match
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-slate-50 rounded-md p-3 text-xs text-slate-600">
                  <p className="font-medium mb-1">Password Requirements:</p>
                  <ul className="space-y-0.5 list-disc list-inside">
                    <li className={formData.password.length >= 8 ? 'text-emerald-600' : ''}>
                      At least 8 characters
                    </li>
                    <li className={/[A-Z]/.test(formData.password) ? 'text-emerald-600' : ''}>
                      One uppercase letter
                    </li>
                    <li className={/[a-z]/.test(formData.password) ? 'text-emerald-600' : ''}>
                      One lowercase letter
                    </li>
                    <li className={/[0-9]/.test(formData.password) ? 'text-emerald-600' : ''}>
                      One number
                    </li>
                    <li className={/[!@#$%^&*(),.?":{}|<>]/.test(formData.password) ? 'text-emerald-600' : ''}>
                      One special character
                    </li>
                  </ul>
                </div>

                <Button 
                  type="submit" 
                  className="w-full govt-btn-primary"
                  disabled={loading}
                  data-testid="register-submit-btn"
                >
                  {loading ? 'Creating Account...' : 'Create Account'}
                </Button>

                <p className="text-center text-sm text-slate-600">
                  Already have an account?{' '}
                  <Link to="/login" className="text-[#003380] hover:underline font-medium">
                    Sign In
                  </Link>
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer - matches landing page */}
      <footer className="bg-[#003380] text-white py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-between items-center">
            <div className="flex items-center gap-4 mb-4 lg:mb-0">
              <img 
                src="/iith-logo.png" 
                alt="IIT Hyderabad" 
                className="h-12 w-auto brightness-0 invert"
              />
            </div>
            <div className="text-sm text-blue-200">
              © {new Date().getFullYear()} Indian Institute of Technology Hyderabad. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
